import {
    Card,
    CardContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {
    DEGREE_F,
    MINIMUM_TEMPERATURE_LABEL,
    NA_TEXT,
    NIGHT_LABEL,
    OVERNIGHT_TEMPERATURES_TITLE,
    OVERNIGHT_TEMPS_ERROR,
    OVERNIGHT_TEMPS_LOADING
} from "../UserVisibleStrings";
import { useSelectedRoute } from "../../features/riskViewer/riskViewerSlice";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useGetOvernightStayListQuery } from "../../services/routing";
import { ComputationStatus } from "../../generated/gpb/api/route";
import React, { useEffect } from "react";

const OvernightsCard = () =>
{
    const routeId = useSelectedRoute();

    const {
        data,
        error,
        isLoading,
        refetch
    } = useGetOvernightStayListQuery( routeId !== undefined ? { routeId: routeId.hexString } : skipToken );

    const computationStatus = data?.overnightComputationStatus;

    useEffect( () =>
    {
        if (
            computationStatus !== undefined &&
            computationStatus !== ComputationStatus.DONE &&
            computationStatus !== ComputationStatus.ERROR
        )
        {
            const interval = setInterval( () =>
            {
                void refetch();
            }, 5000 ); // Re-query every 5 seconds

            return () => clearInterval( interval ); // Cleanup on unmount or computation complete
        }
    }, [ computationStatus, refetch ] );

    if ( isLoading || ( computationStatus !== ComputationStatus.DONE && computationStatus !== ComputationStatus.ERROR ) )
    {
        return (
            <React.Fragment>
                <CircularProgress/>
                <Typography>{OVERNIGHT_TEMPS_LOADING}</Typography>
            </React.Fragment>
        );
    }
    else if ( error || computationStatus === ComputationStatus.ERROR )
    {
        return <Typography>{OVERNIGHT_TEMPS_ERROR}</Typography>;
    }
    else if ( data?.overnights.length === 0 )
    {
        return null;
    }

    return (
        <Card sx = {{
            boxShadow: 3,
            margin: 1,
            backgroundColor: "white",
            borderRadius: "4px",
        }}
        >
            <CardContent sx = {{ padding: "8px" }}>
                <Typography variant = "h5" component = "div" sx = {{ marginBottom: "8px", fontSize: "18px" }}>
                    {OVERNIGHT_TEMPERATURES_TITLE}
                </Typography>
                <TableContainer>
                    <Table size = "small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding = "none"><strong>{NIGHT_LABEL}</strong></TableCell>
                                <TableCell align = "right" padding = "none"><strong>{MINIMUM_TEMPERATURE_LABEL}</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {routeId !== undefined && data?.overnights?.map( ( overnightStay, index ) => (
                                <TableRow key = {index}>
                                    <TableCell padding = "none">
                                        {overnightStay.startTime !== undefined && overnightStay.endTime !== undefined
                                            ? `${new Date( overnightStay.startTime ).toLocaleDateString( "en-US", {
                                                month: "2-digit",
                                                day: "2-digit",
                                            } )} - ${new Date( overnightStay.endTime ).toLocaleDateString( "en-US", {
                                                month: "2-digit",
                                                day: "2-digit",
                                            } )}`
                                            : NA_TEXT}
                                    </TableCell>
                                    <TableCell align = "right" padding = "none">
                                        {overnightStay.temperatureReadings.length > 0
                                            ? ( () =>
                                            {
                                                const temperatures = overnightStay.temperatureReadings
                                                    .map( reading => reading.temperature )
                                                    .filter( ( temp ): temp is number => temp !== undefined );

                                                return temperatures.length > 0
                                                    ? `${Math.min( ...temperatures ).toFixed( 0 )}${DEGREE_F}`
                                                    : NA_TEXT;
                                            } )()
                                            : NA_TEXT}
                                    </TableCell>
                                </TableRow>
                            ) )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default OvernightsCard;
